import {currentUser, getDocumentField, queryCollection, updateDocument} from '@/services/FirestoreService';
import {baseState, baseActions, baseMutations} from '@/store/baseLayer';
import {state as externalState} from '@/external/store/index';
import {MapService} from '@/services/MapService';
import {MapLayer} from '@/services/GraphicService/MapLayer';

/**
 *
 * @typedef {import('@/store/baseLayer.js').AppState} AppState
 *
 * @typedef {Object} ExternalState
 *
 * @typedef {AppState & ExternalState} ExternalAppState
 * @typedef {object} Option
 * @property {string} value the value of the option
 * @property {string} label the label of the option
 * @property {boolean} checked whether the option is selected or not
 *
 * @typedef {Option[]} Options
 *
 */

/** @type {ExternalAppState} */
const state = Object.assign(baseState, {
  userLayers: [],
});

const mutations = Object.assign(baseMutations, {
  addUserLayer: (state, payload) => {
    state.userLayers.push(payload);
  },
});

const actions = Object.assign(baseActions, {
  /**
   *
   * @param {ActionContext} context Vuex context object
   */
  fetchLayers: async ({commit}) => {
    let layers = await queryCollection('beyondMaps-layers', {orderBy: [{field: 'index', order: 'asc'}]});

    if (!layers || !Array.isArray(layers) || layers.length === 0) {
      console.warn('No layers found in the database');
      return;
    }

    layers.forEach((layer, idx) => {
      try {
        layer.map = MapService.getMap();
        layers[idx] = new MapLayer(layer);
      } catch (error) {
        console.warn(`An error occurred while creating MapLayer for layer ${layer.id}`);
        console.error(error);
      }
    });

    if (!externalState.isEditableMap) {
      const publicLayers = layers.filter((layer) => layer.isPublicLayer);
      commit('setLayers', publicLayers);
      return;
    }
    commit('setLayers', layers);
  },
  fetchUsage: async ({commit}) => {},
  /**
   * Fetches user layers from firestore and selects the layers that the user has toggled on for the current map
   * @param {ExternalAppState} state state object
   * @param {ActionContext} context Vuex context object
   */
  fetchUserLayers: async ({state, commit}) => {
    let userLayerSettings = [];
    if (currentUser()) {
      // get the user's layer settings for the current smap
      userLayerSettings = (await getDocumentField('users', currentUser().uid, externalState.mapId))?.userLayerSettings ?? [];
    }

    state.layers.forEach((layer) => {
      const userLayer = userLayerSettings.find((userLayer) => userLayer.id === layer.id);
      // add the layer to the user's layers list, and set the selected state to the user's selected state if defined
      const mapLayer = new MapLayer(layer); // copy the layer to avoid modifying the original
      mapLayer.state = userLayer;
      commit('addUserLayer', mapLayer);
    });
  },
  /**
   * Updates the state of a user layer.
   * @param {ExternalAppState} state state object
   * @param {ActionPayload} payload layer object
   * @param {string} payload.id the id of the layer to toggle
   * @param {Options} payload.options array of options to update
   */
  updateUserLayer: async ({state}, payload) => {
    const userLayer = state.userLayers.find(({id}) => id === payload.id);
    if (!userLayer) return;
    userLayer.state = payload;

    // Only persist the user's layer settings to firestore if they are logged in
    if (!currentUser()?.uid) return;
    const update = {
      [externalState.mapId]: {
        userLayerSettings: state.userLayers.map((layer) => layer.state),
      },
    };
    await updateDocument('users', currentUser().uid, update);
  },
  clearUserLayers: ({state}) => {
    state.userLayers = [];
  },
});

export default {
  namespaced: true,
  state: () => state,
  mutations,
  actions,
};
